/**
 * Prayer rug options (blocks/hadiyati-tapis-options.liquid).
 *
 * - Yes/No choices are native radios: required, no default, keyboard accessible.
 * - "Yes" reveals and enables the related fields (line item properties of the rug, bound to the
 *   product form through the `form` attribute); "No" hides and disables them so nothing is sent.
 * - On submit of the product form (any buy button, including the sticky bar), the choices are
 *   validated first. Paid options are real products: they are added to the cart, linked to the
 *   rug by a hidden `_hdy_lien` property, then the theme's own add to cart runs for the rug.
 */

const SUPPLEMENT_SOURCE = 'hadiyati-tapis-options';

class HadiyatiTapisOptions extends HTMLElement {
  #abort = new AbortController();
  #bypassNextSubmit = false;
  #busy = false;
  /** @type {MutationObserver | null} */
  #observer = null;
  #totalTimer = 0;

  connectedCallback() {
    const { signal } = this.#abort;
    this.formId = this.dataset.formId || '';
    try {
      this.variantPrices = JSON.parse(this.dataset.variantPrices || '{}');
    } catch {
      this.variantPrices = {};
    }

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    // Gift wrap photo preview (only rendered when a real photo is chosen).
    this.addEventListener('click', this.#onZoomClick, { signal });
    // Capture phase on window: the theme dispatches its on:submit / on:click handlers from a
    // capture listener on document, so window is the only place that runs before them. This lets
    // the validation stop an incomplete purchase, and the add-ons be added before the rug.
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    // Buy buttons (main and sticky bar): stop the theme's "added" animations when a choice is missing.
    window.addEventListener('click', this.#onBuyClick, { capture: true, signal });
    // Variant and quantity changes happen in the product form: refresh the total afterwards.
    for (const type of ['change', 'input', 'click']) {
      document.addEventListener(type, this.#scheduleTotal, { signal });
    }

    // Section updates (variant change) can morph this block back to its server state:
    // re-apply the visibility / disabled state that follows from the current answers.
    this.#observer = new MutationObserver(() => this.#syncAll());
    this.#observer.observe(this, { subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });

    this.#syncAll();
    this.#updateTotal();

    // Express: the switch follows the real cart (on load, after each cart change, on page restore).
    this.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-hdy-express-input]')) {
        this.#publishExpress();
        this.#scheduleTotal();
        this.#applyExpressChoice();
      }
    }, { signal });
    document.addEventListener('shopify:cart:lines-update', (event) => {
      const promise = /** @type {any} */ (event).promise;
      Promise.resolve(promise).catch(() => {}).finally(() => setTimeout(() => this.#syncExpress(), 400));
    }, { signal });
    window.addEventListener('pageshow', () => this.#syncExpress(), { signal });
    this.#syncExpress();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#totalTimer);
  }

  get form() {
    return /** @type {HTMLFormElement | null} */ (document.getElementById(this.formId));
  }

  /** @returns {HTMLFieldSetElement[]} */
  get groups() {
    return Array.from(this.querySelectorAll('[data-hdy-group]'));
  }

  /** @param {Element} group */
  #answer(group) {
    const checked = /** @type {HTMLInputElement | null} */ (group.querySelector('.hdy-choice__input:checked'));
    return checked ? checked.value : '';
  }

  /** Shows/enables the details of a group only when its answer is "oui". */
  #syncGroup(group) {
    const yes = this.#answer(group) === 'oui';
    const details = /** @type {HTMLElement | null} */ (group.querySelector('[data-hdy-details]'));
    if (details && details.hidden === yes) details.hidden = !yes;

    for (const field of group.querySelectorAll('[data-hdy-details] input, [data-hdy-details] textarea')) {
      const control = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (field);
      if (control.disabled === yes) control.disabled = !yes;
    }

    const kind = /** @type {HTMLElement} */ (group).dataset.hdyGroup;
    if (kind === 'perso') this.#syncSymbols(yes);
    const required = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-hdy-required="${kind}"]`));
    if (required && required.disabled === yes) required.disabled = !yes;
  }

  #syncAll() {
    this.#observer?.disconnect();
    for (const group of this.groups) this.#syncGroup(group);
    this.#observer?.observe(this, { subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
  }

  /**
   * Opens / closes the enlarged gift wrap photo. The thumbnail is a type="button" outside the
   * option's label, so this never toggles the option, changes the total or submits the form.
   * Escape closes the modal dialog natively; a click on the backdrop closes it too.
   * @param {MouseEvent} event
   */
  #onZoomClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const opener = /** @type {HTMLButtonElement | null} */ (target.closest('[data-hdy-zoom-open]'));
    if (opener) {
      event.preventDefault();
      const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(opener.dataset.hdyZoomOpen || ''));
      if (!dialog || dialog.open) return;
      dialog.addEventListener('close', () => opener.focus(), { once: true });
      dialog.showModal();
      return;
    }
    const dialog = /** @type {HTMLDialogElement | null} */ (target.closest('.hdy-wrap__dialog'));
    if (!dialog) return;
    const box = dialog.getBoundingClientRect();
    const outside =
      target === dialog &&
      (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom);
    if (target.closest('[data-hdy-zoom-close]') || outside) {
      event.preventDefault();
      dialog.close();
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const group = target.closest('[data-hdy-group]');

    if (group && target.classList.contains('hdy-choice__input')) {
      this.#syncAll();
      this.#setError(group, 'choice', '');
    }
    if (target.matches('[data-hdy-color]') && group) this.#setError(group, 'color', '');
    if (target.matches('[data-hdy-symbol]')) this.#syncSymbols(true);
    this.#clearSubmitError();
    this.#updateTotal();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const group = target.closest('[data-hdy-group]');
    if (!group) return;

    if (target.matches('[data-hdy-message]')) {
      const message = /** @type {HTMLTextAreaElement} */ (target);
      const counter = group.querySelector('[data-hdy-count]');
      if (counter) counter.textContent = `${message.value.length}/${message.maxLength > 0 ? message.maxLength : 150}`;
      if (message.value.trim()) this.#setError(group, 'message', '', message);
    }
    if (target.matches('[data-hdy-word]')) {
      const word = /** @type {HTMLInputElement} */ (target);
      if (word.value.trim() && !/\s/.test(word.value.trim())) this.#setError(group, 'word', '', word);
    }
  };

  /**
   * @param {Element} group
   * @param {string} kind
   * @param {string} message - Empty string clears the error.
   * @param {HTMLInputElement | HTMLTextAreaElement} [field]
   */
  #setError(group, kind, message, field) {
    const error = /** @type {HTMLElement | null} */ (group.querySelector(`[data-hdy-error="${kind}"]`));
    if (!error) return;
    if (message) {
      if (kind !== 'choice' && kind !== 'color') error.textContent = message;
      error.hidden = false;
    } else {
      error.hidden = true;
    }
    if (field) field.setAttribute('aria-invalid', message ? 'true' : 'false');
    // The group of choices itself gets the red border (Oui / Non buttons, colour swatches).
    const container = kind === 'choice' ? group.querySelector('.hdy-choice') : kind === 'color' ? group.querySelector('.hdy-swatches') : null;
    if (container) container.toggleAttribute('data-invalid', !!message);
  }

  #clearSubmitError() {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-hdy-submit-error]'));
    if (error) error.hidden = true;
  }

  /**
   * Validates every group and returns the first element to focus, or null when valid.
   * @returns {HTMLElement | null}
   */
  #validate() {
    /** @type {HTMLElement | null} */
    let firstInvalid = null;
    const flag = (/** @type {HTMLElement} */ element) => {
      if (!firstInvalid) firstInvalid = element;
    };

    for (const group of this.groups) {
      const answer = this.#answer(group);
      if (!answer) {
        this.#setError(group, 'choice', 'Veuillez sélectionner Oui ou Non.');
        flag(/** @type {HTMLElement} */ (group.querySelector('.hdy-choice__input')));
        continue;
      }
      if (answer !== 'oui') continue;

      const word = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-hdy-word]'));
      if (word) {
        word.value = word.value.trim();
        if (!word.value) {
          this.#setError(group, 'word', 'Veuillez saisir le mot à inscrire.', word);
          flag(word);
        } else if (/\s/.test(word.value)) {
          this.#setError(group, 'word', 'Indiquez un seul mot.', word);
          flag(word);
        }
      }

      const colors = /** @type {HTMLInputElement[]} */ (Array.from(group.querySelectorAll('[data-hdy-color]')));
      if (colors.length && !colors.some((input) => input.checked)) {
        this.#setError(group, 'color', 'Veuillez choisir la couleur de personnalisation.');
        flag(colors[0]);
      }

      const message = /** @type {HTMLTextAreaElement | null} */ (group.querySelector('[data-hdy-message]'));
      if (message && !message.value.trim()) {
        this.#setError(group, 'message', 'Veuillez écrire le message de votre carte.', message);
        flag(message);
      }
    }
    return firstInvalid;
  }

  /**
   * Chosen symbols, in order (symbol 1 then symbol 2), « Aucun » ignored.
   * @returns {{ rank: string, key: string, label: string }[]}
   */
  #symbols() {
    const chosen = [];
    for (const rank of ['1', '2']) {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-hdy-symbol="${rank}"]:checked`));
      if (input && input.value !== 'aucun') chosen.push({ rank, key: input.value, label: input.dataset.symbolLabel || input.value });
    }
    return chosen;
  }

  /**
   * Keeps the symbol fields of the rug in line with the choices: with the personalisation on
   * « Oui » and at least one symbol, the rug carries « Symboles : Cœur, Étoile » (visible) and the
   * hidden keys that let the cart check the paid lines; otherwise nothing is sent. With the
   * personalisation off, both choices go back to « Aucun ».
   * @param {boolean} active - The personalisation is « Oui »
   */
  #syncSymbols(active) {
    const box = this.querySelector('[data-hdy-symbols]');
    if (!box) return;
    if (!active) {
      for (const input of box.querySelectorAll('[data-hdy-symbol][value="aucun"]')) {
        const radio = /** @type {HTMLInputElement} */ (input);
        if (!radio.checked) radio.checked = true;
      }
    }
    const chosen = active ? this.#symbols() : [];
    const on = chosen.length > 0;
    const set = (/** @type {string} */ selector, /** @type {string} */ value) => {
      const field = /** @type {HTMLInputElement | null} */ (this.querySelector(selector));
      if (!field) return;
      field.value = value;
      field.disabled = !on;
    };
    set('[data-hdy-symbols-value]', chosen.map((symbol) => symbol.label).join(', '));
    set('[data-hdy-symbols-keys]', chosen.map((symbol) => `${symbol.rank}:${symbol.key}`).join('|'));
    const variant = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hdy-symbols-variant]'));
    if (variant) variant.disabled = !on;
    const fields = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hdy-symbols-fields]'));
    if (fields) fields.disabled = !on;

    const total = /** @type {HTMLElement | null} */ (this.querySelector('[data-hdy-symbols-total]'));
    if (total) {
      const cents = chosen.length * (Number(/** @type {HTMLElement} */ (box).dataset.symbolPrice) || 0);
      const text = on
        ? `Symboles : +${new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100)}`
        : '';
      if (total.textContent !== text) total.textContent = text;
      total.hidden = !on;
    }
  }

  /** @returns {{ variant: string, price: number, type: string, order: string, rank?: string, value?: string }[]} */
  #selectedAddons() {
    const addons = [];
    for (const group of this.groups) {
      if (this.#answer(group) !== 'oui') continue;
      const data = /** @type {HTMLElement} */ (group).dataset;
      addons.push({ variant: data.addonVariant || '', price: Number(data.addonPrice) || 0, type: data.addonType || '', order: data.addonOrder || '' });
    }
    for (const box of this.querySelectorAll('.hdy-addon__input:checked:not([data-hdy-express-input])')) {
      const data = /** @type {HTMLInputElement} */ (box).dataset;
      addons.push({ variant: data.addonVariant || '', price: Number(data.addonPrice) || 0, type: data.addonType || '', order: data.addonOrder || '' });
    }
    // One paid line per chosen symbol (rank 1 or 2): two identical symbols stay two lines.
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-hdy-symbols]'));
    const persoGroup = this.querySelector('[data-hdy-group="perso"]');
    if (box && persoGroup && this.#answer(persoGroup) === 'oui') {
      for (const symbol of this.#symbols()) {
        addons.push({
          variant: box.dataset.symbolVariant || '',
          price: Number(box.dataset.symbolPrice) || 0,
          type: 'symbole',
          order: '',
          rank: symbol.rank,
          value: symbol.label,
        });
      }
    }
    return addons.filter((addon) => addon.variant);
  }

  #quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="quantity"]'));
    return Math.max(1, Number(input?.value) || 1);
  }

  #variantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="id"]'));
    return input?.value || '';
  }

  /** The express switch, when the block has one. */
  get #express() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hdy-express-input]'));
  }

  /** Price of the express service to add with this purchase (0 when off or already in the cart). */
  #expressCents() {
    const input = this.#express;
    if (!input || !input.checked || input.dataset.inCart === '1') return 0;
    return Number(input.closest('[data-hdy-express]')?.getAttribute('data-express-price')) || 0;
  }

  /**
   * Express state of the whole order, read from the real cart: when the cart already has the
   * service, the switch shows it (on, locked) and nothing more is added; otherwise it is off,
   * so a removed express is never added back by itself.
   */
  async #syncExpress() {
    const input = this.#express;
    if (!input) return;
    try {
      // @ts-ignore Shopify is set by Shopify on every storefront page
      const root = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })).json();
      const active = cart.items.some((/** @type {any} */ item) => item.properties?._hdy_supplement === 'express');
      const state = /** @type {HTMLElement | null} */ (this.querySelector('[data-hdy-express-state]'));
      this.#cartHasProducts = cart.items.some((/** @type {any} */ item) => !['perso', 'carte', 'emballage', 'prioritaire', 'express', 'symbole'].includes(item.properties?._hdy_supplement));
      if (active) {
        // Already in the order: the switch shows it on, and unticking it removes it.
        input.checked = true;
        input.dataset.inCart = '1';
      } else if (input.dataset.inCart === '1') {
        // It was removed (here or from the cart): back to off, nothing added by itself.
        input.checked = false;
        input.dataset.inCart = '';
      }
      if (state) state.hidden = !active;
      this.#publishExpress();
      this.#scheduleTotal();
    } catch {
      // Without the cart, the switch keeps its current state.
    }
  }

  /** Whether the cart already holds a physical product (set by the last read of the cart). */
  #cartHasProducts = false;

  /**
   * The express service belongs to the whole order. Once the order has products, the switch acts
   * at once: ticking adds the service (once), unticking removes it. On an empty cart it is only a
   * choice, added after the product add succeeded.
   */
  async #applyExpressChoice() {
    const input = this.#express;
    if (!input) return;
    // The state read before this change (from the last cart read) tells whether the service is in the order.
    const inCart = input.dataset.inCart === '1';
    const wanted = input.checked;
    if (inCart && !wanted) {
      document.dispatchEvent(new CustomEvent('hdy:express-remove'));
    } else if (!inCart && wanted && this.#cartHasProducts) {
      const variant = input.closest('[data-hdy-express]')?.getAttribute('data-express-variant');
      if (variant) document.dispatchEvent(new CustomEvent('hdy:express-add', { detail: { variant } }));
    }
  }

  /** Tells the delivery dates which mode is selected. */
  #publishExpress() {
    const input = this.#express;
    const on = !!input && input.checked;
    document.documentElement.toggleAttribute('data-hdy-express', on);
    document.dispatchEvent(new CustomEvent('hdy:express-state', { detail: { on } }));
  }

  // Deferred so the theme has updated the variant id / quantity inputs first.
  #scheduleTotal = () => {
    clearTimeout(this.#totalTimer);
    this.#totalTimer = window.setTimeout(() => this.#updateTotal(), 0);
  };

  #updateTotal() {
    const output = this.querySelector('[data-hdy-total]');
    if (!output) return;
    const base = Number(this.variantPrices[this.#variantId()]) || 0;
    const extras = this.#selectedAddons().reduce((sum, addon) => sum + addon.price, 0);
    // Express is charged once for the whole order, not per unit: added after the quantity.
    const cents = (base + extras) * this.#quantity() + this.#expressCents();
    const currency = this.dataset.currency || 'EUR';
    const total = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency,
    }).format(cents / 100);
    output.textContent = total;

    // The sticky bar shows the same total as the buy box (rug + selected options).
    const sticky = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
    if (sticky && sticky.textContent?.trim() !== total) sticky.textContent = total;
  }

  /** @param {HTMLElement} invalid - First field to complete: shown and focused. */
  #revealInvalid(invalid) {
    // Scroll to the group of the field (its title and its error), below the fixed header.
    const target = invalid.closest('fieldset, .hdy-field') || invalid;
    const header = document.querySelector('header-component, #header-group');
    const headerHeight = header instanceof HTMLElement && getComputedStyle(header).position !== 'static' ? header.getBoundingClientRect().height : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    invalid.focus({ preventScroll: true });
  }

  /**
   * Main add to cart button and sticky bar button: when a required choice is missing, the click
   * is stopped before the theme animates the button / flies the product to the cart, and the
   * visitor is taken to the first field to complete (same validation as the form submission).
   * @param {MouseEvent} event
   */
  #onBuyClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button) return;
    const isMain = button.type === 'submit' && button.form?.getAttribute('id') === this.formId;
    const isSticky = button.matches('sticky-add-to-cart [ref="addToCartButton"]');
    if (!isMain && !isSticky) return;

    const invalid = this.#validate();
    if (!invalid) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#revealInvalid(invalid);
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    // getAttribute: the product form has an input named "id", which shadows form.id.
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.formId) return;

    if (this.#bypassNextSubmit) {
      this.#bypassNextSubmit = false;
      return;
    }

    // An add to cart is already running (options or rug): a second click adds nothing.
    if (this.#busy) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const invalid = this.#validate();
    if (invalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#revealInvalid(invalid);
      return;
    }

    const addons = this.#selectedAddons();
    const link = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hdy-link]'));
    this.#busy = true;
    this.#openDrawerAfterAdd();
    if (!addons.length) {
      if (link) link.disabled = true;
      return; // Nothing to charge: the theme adds the rug as usual.
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const linkId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    if (link) {
      link.value = linkId;
      link.disabled = false;
    }

    const word = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hdy-word]:not(:disabled)'));
    const forRug = [this.dataset.productTitle, word?.value].filter(Boolean).join(' – ');
    const quantity = this.#quantity();
    const items = addons.map((addon) => ({
      id: Number(addon.variant),
      quantity,
      // _hdy_ordre: position of a complement in the block, so the cart lists them in the same order.
      properties: {
        'Pour le tapis': forRug,
        _hdy_lien: linkId,
        _hdy_supplement: addon.type,
        ...(addon.order ? { _hdy_ordre: addon.order } : {}),
        ...(addon.rank ? { _hdy_rang: addon.rank, Symbole: addon.value || '' } : {}),
      },
    }));

    // Theme is declared with `const` by snippets/scripts.liquid: a global binding, not window.Theme.
    // @ts-ignore
    const themeRoutes = typeof Theme === 'undefined' ? null : Theme.routes;
    // @ts-ignore Shopify is set by Shopify on every storefront page
    const addUrl = themeRoutes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'add failed');
        // Options are in the cart: let the theme add the rug (with the same link) and refresh the cart.
        this.#bypassNextSubmit = true;
        form.requestSubmit();
      })
      .catch((error) => {
        console.error(`[${SUPPLEMENT_SOURCE}]`, error);
        if (link) link.disabled = true;
        this.#stopWaitingForAdd();
        const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-hdy-submit-error]'));
        if (message) {
          message.textContent = 'Les options n’ont pas pu être ajoutées au panier. Veuillez réessayer.';
          message.hidden = false;
        }
      });
  };

  /** @type {(() => void) | null} */
  #stopWaitingForAdd = () => {
    this.#busy = false;
  };

  /**
   * Waits for the theme's add to cart of the rug, then opens the cart drawer with the theme's own
   * drawer (its focus, Escape and close handling), only once Shopify confirmed the add: a refused
   * add or a network error leaves the drawer closed. Until then, further submissions are ignored.
   */
  #openDrawerAfterAdd() {
    /** @param {Event} event */
    const onCartUpdate = (event) => {
      const update = /** @type {any} */ (event);
      if (update.action !== 'add') return;
      stop();
      update.promise
        ?.then((/** @type {any} */ result) => {
          if (result?.detail?.didError) return;
          // The product is in the cart: the express service (whole order) can now be added once.
          const express = this.#express;
          if (express && express.checked && express.dataset.inCart !== '1') {
            const variant = express.closest('[data-hdy-express]')?.getAttribute('data-express-variant');
            if (variant) document.dispatchEvent(new CustomEvent('hdy:express-add', { detail: { variant } }));
          }
          const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
          if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
        })
        .catch(() => {})
        .finally(() => {
          this.#busy = false;
        });
    };
    // The theme announces the add with this standard event (resolved with the cart, or an error).
    const timer = setTimeout(() => stop(true), 20000);
    const stop = (release = false) => {
      clearTimeout(timer);
      document.removeEventListener('shopify:cart:lines-update', onCartUpdate);
      if (release) this.#busy = false;
    };
    document.addEventListener('shopify:cart:lines-update', onCartUpdate);
    this.#stopWaitingForAdd = () => stop(true);
  }
}

if (!customElements.get('hadiyati-tapis-options')) {
  customElements.define('hadiyati-tapis-options', HadiyatiTapisOptions);
}
